import { z } from 'zod';
import { areIntervalsOverlapping } from 'date-fns';
import { TenantId, AIDetectableSlideScanIssue, modelsConfigVersion } from './shared';
import { TenantIntegrationConfiguration } from './integration';
import { filterConfig } from './schemas/filter-config';

const coerceInterval = ({ start, end }: { start: string; end: string }) => ({
  start: new Date(start),
  end: new Date(end),
});

const DailyLimit = z
  .number()
  .int()
  .nonnegative();

const dailyLimitConfig = z.object({
  truePositives: DailyLimit.describe('The max number of total true positive slides to anonymize each day'),
  trueNegatives: DailyLimit.describe('The max number of total true negative slides to anonymize each day'),
  falsePositives: DailyLimit.describe('The max number of total false positive slides to anonymize each day'),
  falseNegatives: DailyLimit.describe('The max number of total false negative slides to anonymize each day'),
  ignored: DailyLimit.describe('The max number of total ignored slides to anonymize each day'),
  aiFailures: DailyLimit.describe('The max number of total AI failure slides to anonymize each day'),
});

export const LicenseConfig = z.object({
  anonymizationAllowances: z
    .array(
      z.object({
        period: z.object({
          start: z.iso.datetime({ offset: true })
            .prefault(new Date().toISOString())
            .describe('Timestamp when the period starts'),
          end: z.iso.datetime({ offset: true })
            .prefault(new Date().toISOString())
            .describe('Timestamp when the period ends'),
        }),
        global: z.int()
          .nonnegative()
          .describe('The max number of total slidescans to anonymize for this period'),
        falsePositives: z.int()
          .nonnegative()
          .describe('The max number of total false positive slidescans to anonymize for this period'),
        falseNegatives: z.array(
          z.object({
            type: AIDetectableSlideScanIssue,
            allowance: z.int()
              .nonnegative()
              .describe('The max number of total false negative slidescans for this type to anonymize for this period'),
          }),
        ),
        manuallyAnnotated: z.int()
          .nonnegative()
          .describe(
            'The max number of total slides to anonymize for this period with at least one USER_ONLY issue annotation',
          ),
        daily: dailyLimitConfig,
      }),
    )
    .superRefine((allowances, ctx) => {
      if (
        allowances
          .map(({ period }) => period)
          .some((interval, idx, arr) =>
            arr
              .slice(idx + 1)
              .some((interval2) => areIntervalsOverlapping(coerceInterval(interval), coerceInterval(interval2))),
          )
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'allowance periods cannot overlap',
        });
      }
    }),
});

const InputConfiguration = z.object({
  notificationFilter: z
    .object({
      allowAll: z
        .boolean()
        .prefault(false)
        .describe('Flag to indicate whether or not to allow all the slide scans to process'),
      scanners: z.array(z.string()).optional().describe('The scanner ids to process the slide scan'),
    })
    .optional(),
  inputFilter: filterConfig.optional().describe('Filter configuration for incoming notifications'),
});

export const BarcoConfig = z.object({
  version: z.string().min(1).describe('The version of the barco configuration for the tenant'),
  allowedDataplaneVersions: z.array(z.string()).optional().prefault([]),
  license: LicenseConfig,
  anonymizationSettings: z.object({
    macroMask: z.int().nonnegative().describe('The size of the mask to apply'),
  }),
});

export const TenantConfig = z.object({
  version: z.string().min(1).describe('The version of the tenant configuration'),
  timezone: z
    .string()
    .optional()
    .prefault(() => new Intl.DateTimeFormat().resolvedOptions().timeZone)
    .refine((value) => value === 'UTC' || Intl.supportedValuesOf('timeZone').includes(value), {
      error: 'must be a supported timezone',
    })
    .describe('The timezone of the tenant'),
  customFields: z.partialRecord(z.enum(['customField1', 'customField2', 'customField3']), z.object({ displayName: z.string() }))
    .prefault({}),
  cloudRetentionDays: z.number().int().nonnegative().optional().describe('Number of days to keep data in the cloud before deletion, if not provided the value in env DAYS_TO_KEEP (default 31) will be used.'),
  tileServerUrl: z.url().optional().describe('The tile server URL.').optional(), // When tenant is created, tile server url is unknown
  inputConfiguration: InputConfiguration.optional().describe('The input configuration for the tenant'), // undefined when tenant is created
  integrationConfiguration: TenantIntegrationConfiguration.optional().describe('The integration configuration for the tenant'), // undefined when tenant is created
  metadataConfigRef: z.string().optional().describe('Reference of the metadata config object stored in the config bucket. It will be as path#version'),
});

export type LicenseConfigType = z.infer<typeof LicenseConfig>;
export type BarcoConfigType = z.infer<typeof BarcoConfig>;
export type TenantConfigType = z.infer<typeof TenantConfig>;
export type tenantConfigDailyLimit = z.infer<typeof dailyLimitConfig>;

// The offset value should be at most half the width/height of the glass slide (75x25).
export const ROIOffset = z.object({
  left: z.number().nonnegative().max(37).prefault(0),
  right: z.number().nonnegative().max(37).prefault(0),
  top: z.number().nonnegative().max(12).prefault(0),
  bottom: z.number().nonnegative().max(12).prefault(0),
});
export type ROIOffsetType = z.infer<typeof ROIOffset>;

export const scannerProfile = z
  .object({
    name: z.string().describe('Commercial name of the scanner'),
    vendor: z.string().describe('Vendor of the scanner'),
    offset: ROIOffset.describe('Offset (mm) in the macro image defining the region of interest'),
  })
  .describe('Scanner profile defining the region of interest in the macro image of scanners.');
export type ScannerProfileType = z.infer<typeof scannerProfile>;

export const scannerProfiles = z
  .object({ default: scannerProfile })
  .and(z.record(z.string(), scannerProfile))
  .describe('Maps scanner types (value found in the slide scan metadata) to their respective scanner profiles.');

export type ScannerProfilesType = z.infer<typeof scannerProfiles>;

export const DecisionMode = z.enum(['AUTOMATIC', 'MANUAL', 'IGNORE']).describe(
  'Strategy for handling quality decisions: AUTOMATIC = auto-mark as good/bad, MANUAL = requires manual review, IGNORE = reserved for future functionality and currently not interpreted by the backend (selecting it has no additional effect beyond validation)',
);
export type DecisionModeEnum = z.infer<typeof DecisionMode>;

const InterpretationThreshold = z.object({
  lowerThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "S" (small)'),
  upperThreshold: z.number().nonnegative().describe('Maximum value (included) to consider an issue of level "M" (medium)'),
  automaticRescan: z.boolean().describe('Automatically rescan if above upper threshold - classified as "L" (large)'),
  greenDecisionMode: DecisionMode.prefault('AUTOMATIC').describe('Strategy for handling quality decisions when the QC result is GREEN (severity S)'),
  redDecisionMode: DecisionMode.prefault('AUTOMATIC').describe('Strategy for handling quality decisions when the QC result is RED (severity L)'),
  excluded: z.boolean().prefault(false).describe('Whether to exclude this issue from the overall quality decision, effectively making it an observation-only issue'),
});

export const Interpretation = z.object({
  blur: InterpretationThreshold,
  missingTissue: InterpretationThreshold,
  misalignedCoverSlip: InterpretationThreshold,
  cutOffTissue: InterpretationThreshold,
  markerOnTopOfTissue: InterpretationThreshold,
});
export type InterpretationType = z.infer<typeof Interpretation>;
export type InterpretationThresholdsType = z.infer<typeof InterpretationThreshold>;

export const ModelsConfig = z.object({
  schema_version: z.string().prefault('1.1.0').describe('Version of the models configuration schema'),
  detection: z.object({
    scannerProfiles: scannerProfiles.optional(),
  }),
  interpretation: Interpretation,
});
export type ModelsConfigType = z.infer<typeof ModelsConfig>;

export const FrontendSpecificConfig = z.object({
  tenantId: TenantId,
  displayName: z.string().optional().describe('The display name of the tenant'),
  tenantConfigVersion: TenantConfig.shape.version,
  tileServerUrl: TenantConfig.shape.tileServerUrl,
  timezone: TenantConfig.shape.timezone,
  customFields: TenantConfig.shape.customFields,
  integrationConfiguration: TenantConfig.shape.integrationConfiguration,
  cloudRetentionDays: TenantConfig.shape.cloudRetentionDays,
  allowedDataplaneVersions: BarcoConfig.shape.allowedDataplaneVersions,
});

export type FrontendSpecificConfig = z.infer<typeof FrontendSpecificConfig>;

export const DataplaneSpecificConfig = z.object({
  version: z.string().prefault('0.0.1'),
  tenantId: TenantId,
  triggers: z.array(z.unknown()).prefault([]).describe('List of triggers for the dataplane'),
  filter: InputConfiguration.shape.notificationFilter,
  inputFilter: InputConfiguration.shape.inputFilter,
  anonymizationSettings: BarcoConfig.shape.anonymizationSettings.extend({
    enabled: z.boolean().prefault(false),
  }),
  modelsConfig: ModelsConfig.pick({ detection: true }).partial(),
  modelsConfigVersion,
  bootConfig: z
    .object({
      logLevel: z.string().prefault('info').describe('Log level for the dataplane appliance'),
    })
    .prefault({
      logLevel: 'info',
    }),
});

export type DataplaneSpecificConfigType = z.infer<typeof DataplaneSpecificConfig>;
